import { Image, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { useProgressCarousel } from "@src/appV2/redesign/components/ProgressCarousel";
import { type InternalTimeClockTutorialStep } from "@src/appV2/Shifts/Shift/ShiftState/types";

interface InternalTimeClockTutorialSlideProps {
  action: "clock-in" | "clock-out";
  variant: "ukg" | "nfc";
  step: InternalTimeClockTutorialStep;
  /**
   * e.g. if placeholders is { workplaceName: "Foo" }, then for tutorial headline and body,
   * if "{workplaceName}" is present, it will be replaced by "Foo"
   */
  placeholders: Record<string, string>;
  onClose: () => void;
  onActionPerformed: () => void;
  onSkipNavigationSlides: () => void;
  onTimeclockNotWorking: () => void;
}

function getSlideTexts(
  action: "clock-in" | "clock-out",
  section: InternalTimeClockTutorialStep["tutorialSection"],
  variant: "ukg" | "nfc"
): {
  actionPerformedButton: string;
  secondaryButton: string;
  section: string;
} {
  if (variant === "nfc") {
    const actionPerformedButton = "I'm ready to scan";

    switch (section) {
      case "navigation": {
        return {
          section: "Find the NFC poster",
          secondaryButton: "I'm already at the poster",
          actionPerformedButton,
        };
      }

      case "timeclock-use": {
        return {
          section: "Scan the NFC poster",
          secondaryButton: "Poster is not working",
          actionPerformedButton,
        };
      }

      default: {
        return section satisfies never;
      }
    }
  }

  const actionPerformedButton = action === "clock-in" ? "I have clocked in" : "I have clocked out";

  switch (section) {
    case "navigation": {
      return {
        section: "Go to the facility's time clock",
        secondaryButton: "I'm already at the time clock",
        actionPerformedButton,
      };
    }

    case "timeclock-use": {
      return {
        section: "Using the facility's time clock",
        secondaryButton: "Time clock is not working",
        actionPerformedButton,
      };
    }

    default: {
      return section satisfies never;
    }
  }
}

function applyPlaceholders(text: string, placeholders: Record<string, string>): string {
  return text.replace(/{(\w+)}/g, (_, key: string) => {
    return key in placeholders ? placeholders[key] : key;
  });
}

export function InternalTimeClockTutorialSlide(props: InternalTimeClockTutorialSlideProps) {
  const {
    action,
    variant,
    step,
    placeholders,
    onClose,
    onActionPerformed,
    onSkipNavigationSlides,
    onTimeclockNotWorking,
  } = props;
  const { goToNext, currentIndex, totalSlides } = useProgressCarousel();
  const isLastSlide = currentIndex === totalSlides - 1;
  const texts = getSlideTexts(action, step.tutorialSection, variant);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0, // Important for proper flex behavior
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          paddingLeft: 5,
          height: "40px",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Text variant="caption">{texts.section}</Text>
        <IconButton variant="ghost" iconType="close" aria-label="Close" onClick={onClose} />
      </Box>

      {/* Content Area */}
      <Stack
        sx={{
          display: "flex",
          flex: 1,
          overflow: "auto",
          paddingY: 3,
          paddingX: 7,
          minHeight: 0, // Ensure this can shrink
          gap: 5,
        }}
      >
        <Text variant="h2">{applyPlaceholders(step.tutorialHeadline, placeholders)}</Text>
        <Text variant="h6" sx={{ lineHeight: "23px", whiteSpace: "pre-line" }}>
          {applyPlaceholders(step.tutorialBody, placeholders)}
        </Text>
        <Box
          display="flex"
          flex={1}
          alignItems="center"
          justifyContent="center"
          sx={{
            maxHeight: "45vh",
          }}
        >
          <Image
            src={step.tutorialImage}
            alt={step.tutorialBody}
            width="100%"
            height="100%"
            sx={{
              borderRadius: 2,
              objectFit: "contain",
            }}
          />
        </Box>
      </Stack>

      {/* Fixed Footer */}
      <Box
        sx={(theme) => ({
          flexShrink: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
          padding: 5,
          backgroundColor: theme.palette.background.tertiary,
        })}
      >
        <Stack spacing={2}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => {
              if (isLastSlide) {
                onActionPerformed();
              } else {
                goToNext();
              }
            }}
          >
            {isLastSlide ? texts.actionPerformedButton : "Continue"}
          </Button>

          <Button
            fullWidth
            variant="text"
            size="medium"
            onClick={() => {
              if (step.tutorialSection === "navigation") {
                onSkipNavigationSlides();
              } else {
                onTimeclockNotWorking();
              }
            }}
          >
            {texts.secondaryButton}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
